import { useMutation, useQuery } from '@apollo/client';
import { Euro } from '@mui/icons-material';
import { CircularProgress, Stack } from '@mui/material';
import { FormSchemaProvider, FormSchemaProviderProps } from '@procureai/ui-form-mui';
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  WidgetEditActions,
  useUuid,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNegotiationDetail } from '../Module.hooks';
import { TsrctNegotiationCaseStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { NegotiationTerms } from '~/components/NegotiationTerms/NegotiationTerms';
import { NegotiationTermsForm } from '~/components/NegotiationTermsForm/NegotiationTermsForm';
import {
  termsFormDataDefault,
  TermsFormData,
  TsrctTermsMapper,
  termsFormDataSchema,
} from '~/components/NegotiationTermsForm/NegotiationTermsForm.schema';
import { mapToTsrctNegotiationStrategyBoundaryRuleInput } from '~/components/NegotiationTermsForm/NegotiationTermsForm.util';
import { NEGOTIATION_QUOTE_DETAILS_QUERY } from '~/modules/NegotiationQuoteDetailsWidget/NegotiationQuoteDetailsWidget.query.gql';

import { NEGOTIATION_TERMS_UPDATE_MUTATION } from './NegotiationTermsMutation.gql';
import { NEGOTIATION_TERMS_WIDGET_QUERY } from './NegotiationTermsWidgetQuery.gql';

export interface NegotiationTermsWidgetProps {
  // widget is part of a bigger form -> do not add own form and saving capabilities
  hasParentForm?: boolean;
}

export const NegotiationTermsWidget = ({ hasParentForm }: NegotiationTermsWidgetProps) => {
  const { negotiationId } = useNegotiationDetail();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationTermsWidget' });
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const formId = useUuid();

  const variables = {
    id: negotiationId,
  };

  const { data, loading } = useQuery(NEGOTIATION_TERMS_WIDGET_QUERY, {
    variables,
    skip: !negotiationId,
  });

  const [termsUpdate, { loading: saving }] = useMutation(NEGOTIATION_TERMS_UPDATE_MUTATION, {
    refetchQueries: [
      { query: NEGOTIATION_TERMS_WIDGET_QUERY, variables },
      { query: NEGOTIATION_QUOTE_DETAILS_QUERY, variables },
    ],
    onError,
  });

  const [editOpen, setEditOpen] = useState(hasParentForm);

  const contentData = hasParentForm
    ? { terms: {}, options: {} }
    : TsrctTermsMapper.parse(data?.tesseract.negotiationCase);

  const handleEditToggle = useCallback(() => {
    setEditOpen((prev) => !prev);
  }, []);

  const handleEditApply: FormSchemaProviderProps<TermsFormData>['onSubmit'] = (submitData) => {
    setEditOpen(false);
    if (submitData) {
      (async () => {
        const { errors } = await termsUpdate({
          variables: {
            id: negotiationId,
            strategy: {
              boundaryAdjustments: submitData.boundaryAdjustments,
              boundaryRules: mapToTsrctNegotiationStrategyBoundaryRuleInput(submitData.boundaries),
            },
            options: submitData.options,
          },
        });
        if (!errors?.length) {
          enqueueSuccessSnackbar(t('saveSuccess'));
        }
      })();
    }
  };

  const editDisabled = data?.tesseract.negotiationCase.status !== TsrctNegotiationCaseStatus.INITIALIZED;

  let content = null;

  if (loading) {
    content = (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
        <CircularProgress />
      </Stack>
    );
  } else if (editOpen && contentData) {
    content = <NegotiationTermsForm />;

    if (!hasParentForm) {
      content = (
        <FormSchemaProvider<TermsFormData>
          id={formId}
          data={contentData}
          schema={termsFormDataSchema}
          defaultValues={termsFormDataDefault}
          onSubmit={handleEditApply}
        >
          {content}
        </FormSchemaProvider>
      );
    }
  } else if (contentData) {
    content = <NegotiationTerms data={contentData} />;
  }

  return (
    <>
      <Widget hover={false} data-intercom-target="NegotiationTermsWidget">
        <WidgetHeader
          title={t('title')}
          icon={<WidgetIcon icon={<Euro fontSize="small" />} />}
          actionsRight={
            hasParentForm ? null : (
              <WidgetEditActions
                formId={formId}
                open={editOpen}
                saving={saving}
                disabled={editDisabled}
                onToggle={handleEditToggle}
              />
            )
          }
        />
        <WidgetContent data-testid="NegotiationTermsWidget">{content}</WidgetContent>
      </Widget>
    </>
  );
};
